import { ReactNode } from 'react';
import { isString } from 'lodash';
import styled, { css } from 'styled-components';

import { CloseX } from 'shared/icons/CloseX';
import { Stack } from 'shared/components/Stack';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'shared.components.Window.WindowHeader';

export interface IWindowHeader {
  className?: string;
  closeType?: 'icon' | 'text';
  disableDrag?: boolean;
  handleClose: (e: any) => void;
  isPermanent: boolean;
  isSubtitleOnNewLine?: boolean;
  subtitle?: string | ReactNode;
  title: string | ReactNode;
  xButtonSize?: number;
}

export const WindowHeader = ({
  closeType = 'icon',
  disableDrag = false,
  handleClose,
  isPermanent,
  isSubtitleOnNewLine = false,
  subtitle,
  title,
  xButtonSize,
}: IWindowHeader) => (
  <WindowHeader.Wrapper
    direction="row"
    spacing={closeType === 'icon' ? '24px' : '16px'}
    $isPermanent={isPermanent}
    $disableDrag={disableDrag}
  >
    <WindowHeader.Text direction={isSubtitleOnNewLine ? 'column' : 'row'}>
      {isString(title) ? <h2>{title}</h2> : title}
      {subtitle && <h3>{subtitle}</h3>}
    </WindowHeader.Text>
    <WindowHeader.Close
      aria-label={t(`${TP}.close`, 'Close')}
      data-qa="window_close_button"
      onClick={handleClose}
      $isPermanent={isPermanent}
    >
      {closeType === 'text' ? (
        <span>{t(`${TP}.close`, 'Close')}</span>
      ) : (
        <CloseX size={xButtonSize} />
      )}
    </WindowHeader.Close>
  </WindowHeader.Wrapper>
);

WindowHeader.Wrapper = styled(Stack)<{ $isPermanent: boolean; $disableDrag: boolean }>`
  align-items: baseline;
  padding: 16px;
  ${({ $isPermanent, $disableDrag }) =>
    $isPermanent &&
    !$disableDrag &&
    css`
      cursor: grab;
      &:active {
        cursor: grabbing;
      }
    `};
`;

WindowHeader.Text = styled(Stack)`
  flex-grow: 1;
  align-items: baseline;

  h2 {
    margin: 0 8px 0 0;
  }

  h2,
  h3 {
    font-weight: 400;
    ${variableFontRegular({ size: '9.5px', height: '12px' })};
  }
`;

WindowHeader.Close = styled.button<{ $isPermanent: boolean }>`
  cursor: pointer;
  padding: 0;
  ${variableFontRegular({ size: '9px', height: '9px' })};

  &:hover {
    text-decoration: underline;
  }

  ${({ $isPermanent }) => !$isPermanent && `opacity: 0; cursor: default;`}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    font-size: 9.5px;
  }
`;
